<template>
  <div class="product-editor" :class="{ 'is-disabled': disabled }">
    <div class="editor-grid">
      <label class="field required">
        <span>零件名称</span>
        <el-input v-model="displayName" :disabled="disabled" :maxlength="fieldLimits.NAME" placeholder="如：精密金属轮毂组件">
          <button slot="suffix" type="button" class="ai-polish" :disabled="disabled || polishLoading" @click="$emit('polish', 'name')">
            <i v-if="isPolishing('name')" class="el-icon-loading"></i>
            {{ isPolishing('name') ? 'AI润色中' : 'AI润色' }}
          </button>
        </el-input>
      </label>

      <label class="field required">
        <span>零件分类 / 类目</span>
        <div class="category-select-row">
          <el-select v-model="mainCategoryValue" filterable :disabled="disabled" placeholder="请选择主工艺">
            <el-option v-for="item in categoryOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-select v-model="subCategoryValue" filterable :loading="subCategoryLoading" :disabled="disabled || !mainCategoryValue" placeholder="请选择子工艺">
            <el-option v-for="item in subCategoryOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>
      </label>

      <label class="field required">
        <span>材料</span>
        <el-select
          v-model="materialValue"
          filterable
          allow-create
          default-first-option
          :loading="materialLoading"
          :disabled="disabled || !mainCategoryValue"
          placeholder="请选择"
          @change="handleMaterialChange"
        >
          <el-option v-for="item in innerMaterialOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </label>

      <label class="field">
        <span>价格范围</span>
        <div class="price-range-row">
          <el-input :value="part.minPrice" :disabled="disabled" :maxlength="fieldLimits.PRICE" inputmode="decimal" placeholder="最低价" @input="handleNumericInput('minPrice', $event)" @blur="handleNumericBlur('minPrice')" />
          <b>-</b>
          <el-input :value="part.maxPrice" :disabled="disabled" :maxlength="fieldLimits.PRICE" inputmode="decimal" placeholder="最高价" @input="handleNumericInput('maxPrice', $event)" @blur="handleNumericBlur('maxPrice')" />
          <el-select v-model="part.currency" :disabled="disabled" filterable clearable placeholder="币种">
            <el-option v-for="item in currencyOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>
      </label>

      <label class="field">
        <span>最小起订量</span>
        <div class="min-order-row">
          <el-input :value="part.minOrderAmount" :disabled="disabled" :maxlength="fieldLimits.MIN_ORDER_AMOUNT" inputmode="numeric" placeholder="如：10" @input="handleNumericInput('minOrderAmount', $event)" @blur="handleNumericBlur('minOrderAmount')" />
          <el-select v-model="part.minOrderUnit" :disabled="disabled" filterable clearable placeholder="单位">
            <el-option v-for="item in unitOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>
      </label>

      <label class="field">
        <span>产能</span>
        <div class="capacity-row">
          <el-input :value="part.produceAbility" :disabled="disabled" :maxlength="fieldLimits.PRODUCE_ABILITY" inputmode="numeric" @input="handleNumericInput('produceAbility', $event)" @blur="handleNumericBlur('produceAbility')" />
          <el-select v-model="part.produceAbilityType" :disabled="disabled" filterable clearable>
            <el-option v-for="item in dateUnitOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>
      </label>

      <label class="field">
        <span>关键词</span>
        <el-input
          v-model="keywordsText"
          :disabled="disabled"
          placeholder="最多3个关键词，用逗号分隔"
          @focus="keywordEditing = true"
          @input="handleKeywordsInput"
          @blur="handleKeywordsBlur"
        >
          <button slot="suffix" type="button" class="ai-polish" :disabled="disabled || polishLoading" @click="$emit('polish', 'keywords')">
            <i v-if="isPolishing('keywords')" class="el-icon-loading"></i>
            {{ isPolishing('keywords') ? 'AI润色中' : 'AI润色' }}
          </button>
        </el-input>
      </label>

      <div class="field full required description-field">
        <div class="description-head">
          <span>零件描述</span>
          <button type="button" class="description-polish" :disabled="disabled || polishLoading" @click="$emit('polish', 'content')">
            <i v-if="isPolishing('content')" class="el-icon-loading"></i>
            {{ isPolishing('content') ? 'AI润色中' : 'AI润色' }}
          </button>
        </div>
        <tinyeditor
          :key="descriptionEditorKey"
          class="part-description-editor"
          :resize="true"
          :minHeight="420"
          :maxHeight="760"
          :content="descriptionEditorContent"
          :language="editorLanguage"
          @geEditor="handleDescriptionChange"
        />
      </div>

      <product-faq-editor
        :part="part"
        :site-type="siteType"
        :disabled="disabled"
        :polish-loading="polishLoading"
        :polish-field="polishField"
        @polish="$emit('polish', $event)"
      />
    </div>
  </div>
</template>

<script>
import tinyeditor from '@/components/tyniedor';
import ProductFaqEditor from './ProductFaqEditor.vue';
import { getCategoryMaterialList, getSubCategoryList } from '@/utils/api/product-case';
import { getKeywordText, normalizeKeyword, normalizeRichTextContent, PRODUCT_CASE_FIELD_LIMITS, validatePartNumericField } from '@/utils/product-case/model';
import { SITE_TYPE } from '@/utils/product-case/constants';

export default {
  name: 'ProductEditor',
  components: {
    tinyeditor,
    ProductFaqEditor
  },
  props: {
    part: {
      type: Object,
      required: true
    },
    siteType: {
      type: Number,
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    },
    polishLoading: {
      type: Boolean,
      default: false
    },
    polishField: {
      type: String,
      default: ''
    },
    categoryOptions: {
      type: Array,
      default: () => []
    },
    currencyOptions: {
      type: Array,
      default: () => []
    },
    unitOptions: {
      type: Array,
      default: () => []
    },
    dateUnitOptions: {
      type: Array,
      default: () => []
    },
    materialOptions: {
      type: Array,
      default: () => [
        { label: '铝合金', value: 'aluminum' },
        { label: '不锈钢', value: 'stainlessSteel' },
        { label: '碳钢', value: 'carbonSteel' },
        { label: '铜', value: 'copper' },
        { label: '塑料', value: 'plastic' }
      ]
    }
  },
  data() {
    return {
      subCategoryOptions: [],
      subCategoryLoading: false,
      innerMaterialOptions: this.materialOptions,
      materialLoading: false,
      keywordsText: '',
      keywordEditing: false,
      subCategoryCache: {},
      materialCache: {},
      descriptionEditorContent: '',
      suppressDescriptionSync: false,
      fieldLimits: PRODUCT_CASE_FIELD_LIMITS
    };
  },
  computed: {
    displayName: {
      get() {
        return this.siteType === 2 ? this.part.enName : this.part.cnName;
      },
      set(val) {
        if (this.siteType === 2) this.$set(this.part, 'enName', val);
        else this.$set(this.part, 'cnName', val);
      }
    },
    displayContent: {
      get() {
        return this.descriptionSourceContent;
      },
      set(val) {
        if (this.siteType === 2) this.$set(this.part, 'enContent', val);
        else this.$set(this.part, 'cnContent', val);
      }
    },
    descriptionSourceContent() {
      return this.normalizeEditorContent(this.siteType === 2 ? this.part.enContent : this.part.cnContent);
    },
    mainCategoryValue: {
      get() {
        return this.part.mainCategory || this.findOptionValue(this.categoryOptions, this.part.mainCategoryName) || '';
      },
      set(val) {
        this.handleMainCategoryChange(val);
      }
    },
    subCategoryValue: {
      get() {
        return this.part.subCategory || this.findOptionValue(this.subCategoryOptions, this.part.subCategoryName) || '';
      },
      set(val) {
        this.handleSubCategoryChange(val);
      }
    },
    materialValue: {
      get() {
        return this.part.material || this.part.materialName;
      },
      set(val) {
        this.handleMaterialChange(val);
      }
    },
    dictLanguage() {
      return 'zh';
    },
    editorLanguage() {
      return 'zh_CN';
    },
    descriptionEditorKey() {
      return `description_${this.editorLanguage}`;
    }
  },
  watch: {
    'part.mainCategory': {
      immediate: true,
      handler(value) {
        this.loadSubCategories(value);
        this.loadMaterials(value);
      }
    },
    materialOptions(value) {
      if (!this.innerMaterialOptions.length) this.innerMaterialOptions = value;
    },
    categoryOptions() {
      this.syncMainCategoryByName();
    },
    'part.keywords': {
      immediate: true,
      deep: true,
      handler() {
        if (!this.keywordEditing) this.syncKeywordsTextFromPart();
      }
    },
    descriptionSourceContent: {
      immediate: true,
      handler(value) {
        if (this.suppressDescriptionSync) return;
        this.descriptionEditorContent = value;
      }
    },
    part() {
      this.syncMainCategoryByName();
      this.syncKeywordsTextFromPart();
      this.loadSubCategories(this.part.mainCategory);
      this.loadMaterials(this.part.mainCategory);
    },
    siteType() {
      this.subCategoryCache = {};
      this.materialCache = {};
      this.loadSubCategories(this.part.mainCategory);
      this.loadMaterials(this.part.mainCategory);
    }
  },
  methods: {
    handleNumericInput(field, value) {
      if (this.disabled) return;
      const text = String(value === undefined || value === null ? '' : value);
      const isPrice = field === 'minPrice' || field === 'maxPrice';
      const pattern = isPrice ? /^\d+(\.\d{0,2})?$/ : /^[1-9]\d*$/;
      if (text && !pattern.test(text)) {
        this.$message.warning(isPrice ? '价格请输入数字，最多2位小数' : `${field === 'minOrderAmount' ? '最小起订量' : '产能'}请输入正整数`);
        return;
      }
      this.$set(this.part, field, text);
    },
    handleNumericBlur(field) {
      if (this.disabled) return;
      if (field === 'minPrice' || field === 'maxPrice') {
        const text = String(this.part[field] || '');
        if (text.endsWith('.')) this.$set(this.part, field, text.slice(0, -1));
      }
      const error = validatePartNumericField(field, this.part[field]);
      if (error) this.$message.warning(error);
    },
    isPolishing(field) {
      return this.polishLoading && this.polishField === field;
    },
    handleKeywordsInput(value) {
      if (this.disabled) return;
      const keywords = String(value || '').split(/[,，、;；\n]/)
        .map(item => normalizeKeyword(item))
        .filter(item => item.keyword);
      this.$set(this.part, 'keywords', keywords);
    },
    handleKeywordsBlur() {
      this.keywordEditing = false;
      this.syncKeywordsTextFromPart();
      const error = this.validateKeywordsForEdit();
      if (error) this.$message.warning(error);
    },
    syncKeywordsTextFromPart() {
      this.keywordsText = (this.part.keywords || []).map(getKeywordText).filter(Boolean).join('，');
    },
    validateKeywordsForEdit() {
      const keywords = this.part.keywords || [];
      if (keywords.length > this.fieldLimits.KEYWORD_COUNT) return '关键词最多添加3个';
      const overLengthKeyword = keywords.map(getKeywordText).find(keyword => String(keyword || '').trim().length > this.fieldLimits.KEYWORD);
      return overLengthKeyword ? '单个关键词最多30个字符' : '';
    },
    normalizeEditorContent(value) {
      return normalizeRichTextContent(value);
    },
    findOption(options, value) {
      return (options || []).find(item => item.value === value || item.label === value);
    },
    findOptionValue(options, name) {
      const option = this.findOption(options, name);
      return option ? option.value : '';
    },
    syncMainCategoryByName() {
      if (this.part.mainCategory || !this.part.mainCategoryName) return;
      const option = this.findOption(this.categoryOptions, this.part.mainCategoryName);
      if (option) {
        this.$set(this.part, 'mainCategory', option.value);
        this.$set(this.part, 'mainCategoryName', option.label);
      }
    },
    async loadSubCategories(mainCategory) {
      if (!mainCategory) {
        this.subCategoryOptions = [];
        return;
      }
      const cacheKey = `${mainCategory}_${this.dictLanguage || 'default'}`;
      if (this.subCategoryCache[cacheKey]) {
        this.subCategoryOptions = this.subCategoryCache[cacheKey];
        this.syncSubCategoryByName();
        return;
      }
      this.subCategoryLoading = true;
      try {
        const res = await getSubCategoryList(mainCategory, this.dictLanguage);
        this.subCategoryOptions = (((res || {}).rspListData || {}).list || []).map(item => ({
          label: item.name,
          value: item.codeValue
        }));
        this.$set(this.subCategoryCache, cacheKey, this.subCategoryOptions);
        this.syncSubCategoryByName();
      } catch (error) {
        this.subCategoryOptions = [];
      } finally {
        this.subCategoryLoading = false;
      }
    },
    async loadMaterials(mainCategory) {
      if (!mainCategory) {
        this.innerMaterialOptions = [];
        return;
      }
      const cacheKey = `${mainCategory}_${this.dictLanguage || 'default'}`;
      if (this.materialCache[cacheKey]) {
        this.innerMaterialOptions = this.materialCache[cacheKey];
        this.syncMaterialByName();
        return;
      }
      this.materialLoading = true;
      try {
        const res = await getCategoryMaterialList(mainCategory, this.dictLanguage);
        this.innerMaterialOptions = (((res || {}).rspListData || {}).list || []).map(item => ({
          label: item.name,
          value: item.codeValue
        }));
        this.$set(this.materialCache, cacheKey, this.innerMaterialOptions);
        this.syncMaterialByName();
      } catch (error) {
        this.innerMaterialOptions = [];
      } finally {
        this.materialLoading = false;
      }
    },
    syncSubCategoryByName() {
      if (this.part.subCategory || !this.part.subCategoryName) return;
      const option = this.findOption(this.subCategoryOptions, this.part.subCategoryName);
      if (option) {
        this.$set(this.part, 'subCategory', option.value);
        this.$set(this.part, 'subCategoryName', option.label);
      }
    },
    handleMainCategoryChange(value) {
      if (this.disabled) return;
      const option = this.findOption(this.categoryOptions, value);
      this.$set(this.part, 'mainCategory', option ? option.value : value);
      this.$set(this.part, 'mainCategoryName', option ? option.label : '');
      this.$set(this.part, 'subCategory', null);
      this.$set(this.part, 'subCategoryName', '');
      this.$set(this.part, 'material', null);
      this.$set(this.part, 'materialName', '');
      this.loadSubCategories(option ? option.value : value);
      this.loadMaterials(option ? option.value : value);
    },
    handleSubCategoryChange(value) {
      if (this.disabled) return;
      const option = this.findOption(this.subCategoryOptions, value);
      this.$set(this.part, 'subCategory', option ? option.value : value);
      this.$set(this.part, 'subCategoryName', option ? option.label : '');
    },
    handleMaterialChange(value) {
      if (this.disabled) return;
      const option = this.findOption(this.innerMaterialOptions, value);
      this.$set(this.part, 'material', option ? option.value : value);
      this.$set(this.part, 'materialName', option ? option.label : value);
    },
    syncMaterialByName() {
      if (this.part.material || !this.part.materialName) return;
      const option = this.findOption(this.innerMaterialOptions, this.part.materialName);
      if (option) {
        this.$set(this.part, 'material', option.value);
        this.$set(this.part, 'materialName', option.label);
      }
    },
    handleDescriptionChange(value) {
      if (this.disabled) return;
      const content = this.normalizeEditorContent(value);
      this.suppressDescriptionSync = true;
      this.displayContent = content === '<p><br></p>' ? '' : content;
      this.$nextTick(() => {
        this.suppressDescriptionSync = false;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.product-editor {
  margin-top: 18px;
}
.product-editor.is-disabled {
  opacity: .68;
}
.product-editor.is-disabled .part-description-editor {
  pointer-events: none;
}
.editor-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: 14px 18px;
}
.field {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.field.full {
  grid-column: 1 / -1;
}
.field span {
  color: #071f43;
  font-size: 14px;
  font-weight: 800;
}
.field.required span::after {
  content: ' *';
  color: #e13556;
}
::v-deep .el-input__inner,
::v-deep .el-textarea__inner {
  border-color: #cddbf0;
  color: #071f43;
  font-size: 14px;
}
::v-deep .el-input__inner {
  height: 42px;
  padding-right: 74px;
}
::v-deep .el-select {
  width: 100%;
}
.category-select-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.price-range-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 18px minmax(0, 1fr) 118px;
  align-items: center;
  gap: 8px;
}
.price-range-row b {
  color: #6f7e96;
  font-size: 16px;
  text-align: center;
}
.price-range-row ::v-deep .el-input__inner {
  padding-right: 15px;
}
.min-order-row,
.capacity-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 118px;
  align-items: center;
  gap: 8px;
}
.min-order-row ::v-deep .el-input__inner,
.capacity-row ::v-deep .el-input__inner,
.price-range-row ::v-deep .el-select .el-input__inner {
  padding-right: 30px;
}
.ai-polish,
.textarea-polish {
  border: 0;
  background: transparent;
  color: #1a5cff;
  font-weight: 800;
  font-size: 13px;
}
.ai-polish {
  height: 100%;
  padding: 0 10px;
}
.ai-polish:not(:disabled),
.description-polish:not(:disabled) {
  cursor: pointer;
}
.ai-polish:disabled,
.description-polish:disabled {
  cursor: not-allowed;
}
.ai-polish i,
.description-polish i {
  margin-right: 4px;
}
.description-field {
  gap: 8px;
}
.description-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.description-polish {
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 3px 8px;
  background: transparent;
  color: #1a5cff;
  font-size: 13px;
  font-weight: 800;
}
.description-polish:hover,
.description-polish:focus {
  border-color: #cfe0ff;
  background: #f3f7ff;
}
.part-description-editor {
  line-height: normal;
}
.part-description-editor ::v-deep .tox-tinymce {
  border-color: #cddbf0;
  border-radius: 8px;
}
.part-description-editor ::v-deep .tox .tox-toolbar,
.part-description-editor ::v-deep .tox .tox-toolbar__overflow,
.part-description-editor ::v-deep .tox .tox-toolbar__primary {
  background: #fbfdff;
}
.part-description-editor ::v-deep .tox .tox-edit-area {
  border-top: 1px solid #d9e2f0;
}
.part-description-editor ::v-deep .tox .tox-edit-area__iframe {
  background: #fff;
}
@media (max-width: 1360px) {
  .editor-grid {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
}
</style>
